import React, { useState } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';

import { Grid, Box, Card, CardContent, Typography, Divider } from '@mui/material';

import { reportPageStyles as styles } from '../styles';
import { futureProjections } from '../../../configs/FinancialKPIConfig';
import { COLOR_PRIMARY, exchangeRates } from 'src/const';
import { DropdownSelect } from 'src/App/Components/Inputs';
import { COLOR_GREY_400 } from 'src/colors';
import { abbreviateNumber, convertCurrency, isObject } from 'src/utils/functions';
import DisabledBlurCard from '../../../UtilityComponents/BlurCard';
import { useSelector } from 'react-redux';

const renderChart = (
  graphKey,
  kpiData,
  currency,
  handleLoginOrPricingModalOpen,
  isUserSubscribed
) => {
  const graphConfig = futureProjections[graphKey];
  const { key, unit, legendMap, barColorsMap } = graphConfig;
  const chartData = kpiData[key];

  if (!chartData) return null;

  const formattedData = Object.entries(chartData).map(([xValue, yValues]) => ({
    name: xValue,
    ...(typeof yValues === 'object' ? yValues : { value: yValues }),
  }));

  const CustomTooltip = props => {
    const { active, payload, label, currency } = props;

    if (active && payload && payload.length) {
      return (
        <Card
          style={{
            backgroundColor: '#ffffff',
            padding: '10px',
            borderRadius: '8px',
            boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
          }}
        >
          <CardContent>
            <Typography variant="body2" style={{ fontWeight: 600, marginBottom: '10px' }}>
              {label}
            </Typography>

            <Divider style={{ background: '#ccc', marginTop: 10, marginBottom: 10 }} />

            {payload.map((entry, index) => (
              <Typography
                key={`tooltip-${index}`}
                variant="body2"
                style={{
                  color: entry.color || '#000',
                  fontWeight: 500,
                  marginBottom: '5px',
                }}
              >
                {entry.name}:{' '}
                {entry.unit != '%'
                  ? entry.unit + abbreviateNumber(convertCurrency(currency, entry.value), true)
                  : abbreviateNumber(entry.value, true)}{' '}
                {entry.unit == '%' ? entry.unit : ''}
              </Typography>
            ))}
          </CardContent>
        </Card>
      );
    }

    return null;
  };

  return (
    <Box key={key} sx={{ position: 'relative' }}>
      <ResponsiveContainer width="100%" height={300}>
        <BarChart
          data={formattedData}
          maxBarSize={20}
          style={{
            fontFamily: 'Roboto',
            position: 'relative',
          }}
        >
          <CartesianGrid
            strokeDasharray="6 6"
            stroke={COLOR_GREY_400}
            vertical={false}
            horizontal={true}
          />
          <XAxis
            dataKey="name"
            axisLine={false}
            tickLine={false}
            textAnchor="middle"
            style={{
              fontSize: '0.75rem',
              fontFamily: 'Roboto',
            }}
          />

          <YAxis
            axisLine={false}
            tickLine={false}
            tickFormatter={props =>
              (unit == '$' ? exchangeRates[currency].symbol : '') +
              abbreviateNumber(unit == '$' ? convertCurrency(currency, props) : props, true) +
              (unit != '$' ? unit : '')
            }
            style={{
              fontSize: '0.75rem',
              fontFamily: 'Roboto',
            }}
          />
          <Tooltip
            isAnimationActive={false}
            content={<CustomTooltip active payload label currency={currency} />}
          />
          {isObject(chartData[Object.keys(chartData)[0]]) ? (
            Object.keys(chartData[Object.keys(chartData)[0]]).map((barKey, index) => {
              if (barKey === 'total_revenue') return null;
              return (
                <Bar
                  key={barKey}
                  dataKey={barKey}
                  stackId={'revenueStack'}
                  fill={barColorsMap[barKey] || COLOR_PRIMARY}
                  radius={
                    index === Object.keys(chartData).length - 2 ? [100, 100, 0, 0] : [0, 0, 0, 0]
                  }
                  barSize={13}
                  unit={unit === '$' ? exchangeRates[currency]?.symbol : unit}
                  name={legendMap[barKey] || barKey}
                  style={{ cursor: 'pointer' }}
                />
              );
            })
          ) : (
            <Bar
              dataKey="value"
              fill={COLOR_PRIMARY}
              barSize={13}
              unit={unit == '$' ? exchangeRates[currency].symbol : unit}
              style={{ cursor: 'pointer' }}
              radius={[100, 100, 0, 0]}
              name={legendMap['value'] || 'value'}
            />
          )}
          <Legend iconType="circle" wrapperStyle={{ fontSize: '0.75rem', fontFamily: 'Roboto' }} />
        </BarChart>
      </ResponsiveContainer>

      {!isUserSubscribed && (
        <DisabledBlurCard
          subscriptionType="Expert"
          onDisabledOptionClick={() => {
            handleLoginOrPricingModalOpen('open');
          }}
          addBlur="2px"
        />
      )}
    </Box>
  );
};

const GraphSection = ({
  kpiData,
  reportType,
  currency,
  handleLoginOrPricingModalOpen,
  isUserSubscribed,
}) => {
  const defaultProjectionByReportTypeMap = Object.keys(futureProjections).reduce(
    (acc, graphKey) => {
      const graphConfig = futureProjections[graphKey];
      const { showAsDeafaultIn } = graphConfig;
      acc[showAsDeafaultIn] = graphKey;
      return acc;
    },
    {}
  );
  const [selectedGraph, setSelectedGraph] = useState(defaultProjectionByReportTypeMap[reportType]);

  return (
    <Grid
      container
      alignItems="flex-start"
      justifyContent="center"
      xs
      sx={{ ...styles.paper, ...styles.reportKPISection }}
      gap={2}
    >
      <Grid
        container
        item
        xs={12}
        direction="row"
        justifyContent="space-between"
        sx={{ marginBottom: 2 }}
      >
        <Box
          sx={{
            height: '100%',
          }}
        >
          <DropdownSelect
            name="Projection Type"
            value={selectedGraph}
            setValue={setSelectedGraph}
            options={Object.keys(futureProjections).reduce((acc, graphKey) => {
              const graphConfig = futureProjections[graphKey];
              const { displayName, allowedModes } = graphConfig;
              if (allowedModes && !allowedModes.includes(reportType)) return acc;
              acc[graphKey] = displayName;
              return acc;
            }, {})}
          />
        </Box>
      </Grid>

      <Grid container item xs={12} direction="column" gap={2}>
        {renderChart(
          selectedGraph,
          kpiData,
          currency,
          handleLoginOrPricingModalOpen,
          isUserSubscribed
        )}
      </Grid>
    </Grid>
  );
};

export default GraphSection;
